import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  clientTruthOwner,
  deriveStepTruth,
  factsFromStaffDetail,
  stepSignalsFromFacts,
  truthDisplayStatus,
  type StaffStepTruth,
  type StaffTruthOwner
} from "../../shared/staff-lifecycle";
import { RAIL_STEP_KEYS, type RailStepKey } from "../rail-model";
import { staffCurrentStep, staffStepStatus } from "./core";
import type { StaffDetail, StaffRailStep } from "./model";
import { materialKindsForStep } from "./PhaseWorkspace";
import type { ProductionWait } from "../../shared/dfy-prerequisites";

export type StaffActionKind =
  | "partner_action"
  | "automation"
  | "waiting_client"
  | "blocked"
  | "locked"
  | "complete";

export interface StaffNextAction {
  kind: StaffActionKind;
  step: string;
  titleKey: string;
  bodyKey?: string;
  ctaKey?: string;
  count: number;
  scope?: "global" | "local";
  /** Canonical owner of the truth behind the action. `blocked` alone does not
   * say whether a machine failed or the team owes an output, and the hero's
   * signpost has to name that (fix-partak-stavy, P-08). */
  owner?: StaffTruthOwner;
  production?: ProductionWait;
}

/**
 * What the hero says about work that is NOT on the step the partner opened.
 *
 * The old sentence was "Další úkol týmu: Odblokuj krok, chybí povinný výstup"
 * - an ORDER, printed on thirteen of fourteen steps, that never named the step
 * it meant, and that the step itself contradicts: "Nápravu dokončí tým ... nic
 * dalšího tu dělat nemusíš". A signpost states a fact and names its
 * destination; only then is it worth clicking (audit P-08).
 */
export function staffElsewhereStateKey(
  action: Pick<StaffNextAction, "kind" | "owner">
): string {
  if (action.kind === "blocked") {
    return action.owner === "alex"
      ? "partnerOS.clientHero.elsewhereState.blockedAlex"
      : "partnerOS.clientHero.elsewhereState.blockedTeam";
  }
  return `partnerOS.clientHero.elsewhereState.${action.kind}`;
}

const RAIL_STEP_KEY_SET = new Set<string>(RAIL_STEP_KEYS as readonly string[]);

/**
 * Whether the hero shows the signpost at all. It points SOMEWHERE ELSE, so it
 * needs a step the rail can actually name, a step other than the open one, and
 * a state worth walking to: "everything is done" and "not open yet" are not
 * next actions.
 */
export function staffShowsElsewhere(
  secondary: StaffNextAction | undefined,
  activeStep: string
): secondary is StaffNextAction & { step: RailStepKey } {
  if (!secondary) return false;
  if (secondary.step === activeStep) return false;
  if (secondary.kind === "complete" || secondary.kind === "locked") return false;
  return RAIL_STEP_KEY_SET.has(secondary.step);
}

/** "Jinde u klienta: Trénink · tým dodělává výstup" - place, then fact. */
export function staffElsewhereLabel(
  action: Pick<StaffNextAction, "kind" | "owner" | "step">,
  t: (key: string) => string
): string {
  const lead = t("partnerOS.clientHero.elsewhereLead");
  const step = t(`rail.labels.${action.step}`);
  return `${lead}: ${step} · ${t(staffElsewhereStateKey(action))}`;
}

/**
 * The global hero consumes the SAME canonical truths as the rail and the
 * matrix (fixr5, order 1): no private derivation over raw sections or
 * deliverables. Priority mirrors the truth precedence: blocked, working,
 * on deck for the team, then the client's own current step.
 */
export function nextBestStaffAction(detail: StaffDetail, activeStep?: string): StaffNextAction {
  if (activeStep) return staffActionForStep(detail, activeStep);
  const facts = factsFromStaffDetail(detail);
  const truths = detail.steps.map((step) => deriveStepTruth(stepSignalsFromFacts(facts, step.key)));
  const pendingCount = facts.openFeedback.length;

  const blocked = truths.find((truth) => truth.status === "blocked");
  if (blocked) {
    if (blocked.production) return productionAction(blocked);
    return {
      kind: "blocked",
      step: blocked.key,
      titleKey: blocked.owner === "alex"
        ? "partnerOS.clientHero.failed"
        : "partnerOS.clientHero.stepBlocked",
      count: 1,
      owner: blocked.owner
    };
  }
  const working = truths.find((truth) => truth.status === "working");
  if (working) {
    return {
      kind: "automation",
      step: working.key,
      titleKey: "partnerOS.clientHero.automation",
      count: 1,
      owner: working.owner
    };
  }
  const onDeckSteps = truths.filter((truth) => truth.status === "on_deck");
  const onDeck = onDeckSteps.find((truth) => truth.key !== "stripe") ?? onDeckSteps[0];
  if (onDeck) {
    if (onDeck.production) return productionAction(onDeck);
    if (onDeck.key === "stripe") {
      // Stripe is on deck for the team only when the connect link is missing.
      return {
        kind: "partner_action",
        step: "stripe",
        titleKey: "partnerOS.clientHero.stripeLink",
        ctaKey: "partnerOS.clientHero.addStripeLink",
        count: 1
      };
    }
    // A podnět with no material comes from beta testing; the collection step
    // is where the partner decides about it (audit a2-02).
    const step = onDeck.key === "beta"
      && truths.some((truth) => truth.key === "feedback" && truth.status === "on_deck")
      ? "feedback"
      : onDeck.key;
    return {
      kind: "partner_action",
      step,
      titleKey: "partnerOS.clientHero.review",
      count: pendingCount
    };
  }

  const current = staffCurrentStep(detail.steps);
  const currentTruth = current ? truths.find((truth) => truth.key === current.key) : undefined;
  if (!current || currentTruth?.status === "done" || currentTruth?.status === "done_ahead") {
    return {
      kind: "complete",
      step: current?.key ?? "home",
      titleKey: "partnerOS.clientHero.complete",
      count: 0
    };
  }
  const callWait = current.key === "onboarding_call"
    ? {
        titleKey: "partnerOS.clientHero.waitingOnboarding",
        bodyKey: "partnerOS.clientHero.waitingOnboardingNext"
      }
    : current.key === "marketing_call"
      ? {
          titleKey: "partnerOS.clientHero.waitingMarketing",
          bodyKey: "partnerOS.clientHero.waitingMarketingNext"
        }
      : null;
  return {
    kind: "waiting_client",
    step: current.key,
    titleKey: callWait?.titleKey ?? "partnerOS.clientHero.waiting",
    bodyKey: callWait?.bodyKey,
    count: 0
  };
}

/** Canonical answer to "whose move is it" for the whole client. */
export function staffClientOwner(detail: StaffDetail): "partner" | "client" | "alex" {
  const facts = factsFromStaffDetail(detail);
  return clientTruthOwner(
    detail.steps.map((step) => deriveStepTruth(stepSignalsFromFacts(facts, step.key)))
  );
}

/** The one mapping from canonical truth to the console's action vocabulary. */
export function staffActionKindForTruth(truth: StaffStepTruth): StaffActionKind {
  switch (truth.status) {
    case "locked": return "locked";
    case "blocked": return "blocked";
    case "working": return "automation";
    case "on_deck": return "partner_action";
    case "done":
    case "done_ahead": return "complete";
    default: return "waiting_client";
  }
}

/** Canonical truth for one step, derived from the detail payload through the
 * shared module (shared/staff-lifecycle.ts). Every surface reads THIS. */
export function staffStepTruth(detail: StaffDetail, step: string): StaffStepTruth {
  return deriveStepTruth(stepSignalsFromFacts(factsFromStaffDetail(detail), step));
}

export function staffActionForStep(detail: StaffDetail, step: string): StaffNextAction {
  const truth = staffStepTruth(detail, step);
  if (truth.production) return { ...productionAction(truth), scope: "local" };
  const kind = staffActionKindForTruth(truth);
  return {
    kind,
    step,
    titleKey: `partnerOS.clientHero.local.${kind}`,
    count: kind === "blocked" ? 1 : 0,
    scope: "local",
    owner: truth.owner
  };
}

function productionAction(truth: StaffStepTruth): StaffNextAction {
  return {
    kind: staffActionKindForTruth(truth),
    step: truth.key,
    titleKey: `partnerOS.clientHero.production.${truth.production?.targetState ?? truth.production?.prerequisite?.state ?? "ready"}`,
    bodyKey: "partnerOS.clientHero.production.body",
    count: 1,
    owner: truth.owner,
    production: truth.production
  };
}

export function staffStepsWithLifecycle(detail: StaffDetail): StaffRailStep[] {
  const facts = factsFromStaffDetail(detail);
  return detail.steps.map((step) => {
    const truth = deriveStepTruth(stepSignalsFromFacts(facts, step.key));
    return {
      ...step,
      staffStatus: truthDisplayStatus(truth, staffStepStatus(step)),
      owner: truth.owner,
      visibleVersion: truth.visibleVersion,
      aheadOfUnlock: truth.aheadOfUnlock,
      ...(truth.production ? { production: truth.production } : {})
    };
  });
}

/**
 * Backwards-compatible adapter: the validation-aware lifecycle of the
 * non-material steps, now answered by the canonical truth module.
 */
export function nonMaterialStepLifecycle(detail: StaffDetail, step: string): StaffActionKind | null {
  if (!["training", "stripe", "beta", "feedback"].includes(step)) return null;
  return staffActionKindForTruth(staffStepTruth(detail, step));
}

export function ClientCockpitHero(props: {
  action: StaffNextAction;
  t: (key: string) => string;
  onOpen: (step: string) => void;
  /** The step the workspace below is already showing. */
  activeStep?: string;
  secondaryAction?: StaffNextAction;
}) {
  // When the partner is already standing on the recommended step, "Otevřít
  // krok" led back to the page they were on, competing with the real action
  // inside the workspace (audit P1). Then the card is a signpost, not a CTA.
  const alreadyHere = props.activeStep === props.action.step;
  const prerequisiteStep = props.action.production?.prerequisite?.step;
  const actionTitle = props.t(props.action.titleKey).replace("{prerequisite}",
    prerequisiteStep ? props.t(`rail.labels.${prerequisiteStep}`) : "");
  return (
    <article
      className={`staff-next-action ${props.action.kind}${alreadyHere ? " here" : ""}`}
      data-staff-state={props.action.kind}
    >
      <div>
        <p className="panel-label">{props.t(props.action.scope === "local"
          ? "partnerOS.clientHero.localKicker"
          : "partnerOS.clientHero.kicker")}</p>
        <h3>{actionTitle}</h3>
        {props.action.bodyKey && <p className="staff-next-action-body">{props.t(props.action.bodyKey)}</p>}
      </div>
      {prerequisiteStep ? (
        <button className="primary-button" onClick={() => props.onOpen(prerequisiteStep)} type="button"
          data-production-prerequisite={prerequisiteStep}>
          {props.t("partnerOS.clientHero.production.open")}
        </button>
      ) : alreadyHere && !props.action.bodyKey ? (
        <span className="staff-next-action-here">{props.t("partnerOS.clientHero.here")}</span>
      ) : !alreadyHere ? (
        <button
          className="primary-button"
          onClick={() => props.onOpen(props.action.step)}
          type="button"
        >
          {props.t(props.action.ctaKey ?? "partnerOS.clientHero.open")}
        </button>
      ) : null}
      {staffShowsElsewhere(props.secondaryAction, props.action.step) && (
        <button
          className="quiet-link staff-next-action-secondary"
          data-staff-elsewhere={props.secondaryAction!.step}
          onClick={() => props.onOpen(props.secondaryAction!.step)}
          type="button"
        >
          {/* A signpost, not an order: it names WHERE the work is and states
              WHAT is happening there, in the owner's voice (audit a3-03 kept
              the owner, P-08 added the step name and dropped the imperative
              the destination step itself contradicts). */}
          {staffElsewhereLabel(props.secondaryAction!, props.t)}
        </button>
      )}
    </article>
  );
}

export function ServiceDrawer(props: {
  t: (key: string) => string;
  children?: ReactNode;
  /**
   * F138a: REQUIRED. `partnerOS.service.title` ("Servis") and its hint were
   * the defaults nobody used - all three call sites passed their own scope -
   * and the audit found them orphaned (sekce 9). The keys are deleted, so a
   * drawer without a scope of its own is now a compile error instead of a
   * landmark named after a menu that no longer exists.
   */
  titleKey: string;
  hintKey: string;
  /**
   * B2 (audit F115): a control OUTSIDE the drawer may ask it to open. The
   * blocked step's "Otevřít Zdroje" switched the tab underneath a shut
   * drawer, so the admin clicked and nothing moved on screen - a dead control
   * built out of two live halves. Bumping the token opens the drawer and
   * brings it into view; the mount-once rule below is untouched.
   */
  openToken?: number;
}) {
  // 🔴 `summary { display: flex }` removes the browser's disclosure marker, so
  // this bar (behind which the WHOLE second level lives: Přehled, Podněty,
  // Výstupy, Odpovědi, Zadání pro výrobu, Změny profilu, Zdroje, Hovory a
  // kódy, Aktivita) rendered as a static heading with a caption - nothing said
  // it opened, and nothing changed when it did (audit P-11). The caret is an
  // explicit element, never `::marker`, because the flex summary has none;
  // `.disclosure-caret` is the shared piece for the next flex summary too.
  // 🔴 S3-05: a closed drawer does not fetch. The Servis drawer holds eleven
  // panels and every one of them connected and loaded while the drawer was
  // shut, so simply OPENING the administrator console fired twelve admin
  // requests (2 587 ms of request time, 32 kB, one of them a census over 4 869
  // notification rows) for a surface the reader might not touch that day. The
  // slowest start in the product belonged to the person who opens it most.
  //
  // Mounted once and kept: closing the drawer must not throw away a half-typed
  // form or force a second round of loading. `<details>` fires `toggle` on the
  // element itself, so this reads the real open state and never guesses.
  const [opened, setOpened] = useState(false);
  const drawer = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (!props.openToken || !drawer.current) return;
    drawer.current.open = true;
    setOpened(true);
    drawer.current.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [props.openToken]);
  return (
    <details className="staff-service-drawer" ref={drawer}
      onToggle={(event) => { if ((event.currentTarget as HTMLDetailsElement).open) setOpened(true); }}>
      <summary>
        <span className="staff-service-drawer-text">
          <span>{props.t(props.titleKey)}</span>
          <small>{props.t(props.hintKey)}</small>
        </span>
        <span aria-hidden="true" className="disclosure-caret">
          <svg viewBox="0 0 24 24" focusable="false"><path d="m8 5 8 7-8 7" /></svg>
        </span>
      </summary>
      <div className="staff-service-content">{opened ? props.children : null}</div>
    </details>
  );
}
